import { faLinkSlash, faPlus, faStar, faTag } from "@fortawesome/free-solid-svg-icons";
import { useMemo, useState } from "react";
import { Alert } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import { ConfirmationModel } from "../../../components/features/common";
import { ColumnRender, FilterType, IColumn, IFilter, IPagedListAction, SortOrder } from "../../../components/UI/Declarations";
import { PagedList } from "../../../components/UI/PagedList/PagedList";
import type { ICustomerLocationAssociationRow } from "../../../customTypings/customer/Customer";
import { customerLocationAssociationsService } from "../../../services/customers/customerLocationAssociations.service";
import { formatPostalAddress, getUserFacingErrorMessage, PAGED_LIST_MAIN_LOCATION_ROW_INDICATOR, showToastError, showToastSuccess, LOCATION_RELATED_ENTITY_LIFECYCLE, relatedEntityTextColumn } from "../../../utils";
import { useCustomerEditOutletContext } from "../customerEditOutletContext";
import { AddCustomerLocationModal } from "@/pages/CustomerEditPage/locations/AddCustomerLocationModal";
import { EditCustomerLocationLabelModal } from "@/pages/CustomerEditPage/locations/EditCustomerLocationLabelModal";

const CustomerLocationsView = () => {
    const navigate = useNavigate();
    const { siteId, customerId, customerName, metaDeleted, readOnlyForm } = useCustomerEditOutletContext();
    const [refreshNumber, setRefreshNumber] = useState(0);
    const [showAddModal, setShowAddModal] = useState(false);
    const [confirmRow, setConfirmRow] = useState<ICustomerLocationAssociationRow | null>(null);
    const [confirmSetMainRow, setConfirmSetMainRow] = useState<ICustomerLocationAssociationRow | null>(null);
    const [editLabelRow, setEditLabelRow] = useState<ICustomerLocationAssociationRow | null>(null);

    const columns: IColumn[] = useMemo(
        () => [
            relatedEntityTextColumn({
                member: "locationName",
                title: "Location",
                width: 220,
                relatedEntityLifecycle: LOCATION_RELATED_ENTITY_LIFECYCLE,
            }),
            {
                member: "address",
                renderAs: ColumnRender.customRenderer,
                title: "Address",
                width: 280,
                customRenderer: (item: ICustomerLocationAssociationRow) => (
                    <td className="text-start text-muted small">{formatPostalAddress(item) || "-"}</td>
                ),
            } as IColumn,
            { member: "label", renderAs: ColumnRender.text, title: "Label", width: 160 } as IColumn,
        ],
        []
    );

    const filters: IFilter[] = useMemo(
        () => [
            { title: "Location", member: "locationNameSearch", type: FilterType.text } as IFilter,
            { title: "Address", member: "addressSearch", type: FilterType.text } as IFilter,
            { title: "Label", member: "labelSearch", type: FilterType.text } as IFilter,
            {
                title: "Deleted",
                member: "locationIsDeleted",
                type: FilterType.select,
                options: { All: null, Yes: true, No: false },
                defaultValue: false,
            } as IFilter,
            {
                title: "Enabled",
                member: "locationIsEnabled",
                type: FilterType.select,
                options: { All: null, Yes: true, No: false },
            } as IFilter,
        ],
        []
    );

    const headActions: IPagedListAction[] = useMemo(
        () =>
            readOnlyForm
                ? []
                : [
                      {
                          text: "Add Location",
                          icon: faPlus,
                          primary: true,
                          onClick: () => setShowAddModal(true),
                      } as IPagedListAction,
                  ],
        [readOnlyForm]
    );

    const itemActions: IPagedListAction[] = useMemo(
        () => [
            {
                text: "Edit",
                hideWhenMember: "isMainLocation",
                onClick: (item: ICustomerLocationAssociationRow) => navigate(`/locations/${item.locationId}`),
                primary: true,
            } as IPagedListAction,
            ...(readOnlyForm
                ? []
                : [
                      {
                          text: "Edit label",
                          icon: faTag,
                          onClick: (item: ICustomerLocationAssociationRow) => setEditLabelRow(item),
                      } as IPagedListAction,
                      {
                          text: "Set as primary address",
                          icon: faStar,
                          hideWhenMember: "isMainLocation",
                          onClick: (item: ICustomerLocationAssociationRow) => setConfirmSetMainRow(item),
                      } as IPagedListAction,
                      {
                          text: "Unlink",
                          icon: faLinkSlash,
                          hideWhenMember: "isMainLocation",
                          itemClassName: "text-danger",
                          onClick: (item: ICustomerLocationAssociationRow) => setConfirmRow(item),
                      } as IPagedListAction,
                  ]),
        ],
        [readOnlyForm, navigate]
    );

    const runRemove = () => {
        if (!confirmRow) return;
        customerLocationAssociationsService
            .unlinkAssociation(siteId, customerId, confirmRow.id)
            .then(() => {
                showToastSuccess("Location unlinked.");
                setRefreshNumber((n) => n + 1);
                setConfirmRow(null);
            })
            .catch((e: unknown) => {
                showToastError(getUserFacingErrorMessage(e, "Could not unlink location."));
                setConfirmRow(null);
            });
    };

    const runSetMain = () => {
        if (!confirmSetMainRow) return;
        customerLocationAssociationsService
            .setMainAssociation(siteId, customerId, confirmSetMainRow.id)
            .then(() => {
                showToastSuccess("Primary address updated.");
                setRefreshNumber((n) => n + 1);
                setConfirmSetMainRow(null);
            })
            .catch((e: unknown) => {
                showToastError(getUserFacingErrorMessage(e, "Could not set primary address."));
                setConfirmSetMainRow(null);
            });
    };

    return (
        <div className="mt-4">
            <h3 className="mb-3">{customerName} Locations</h3>
            {readOnlyForm && (
                <Alert variant="warning" className="mb-3">
                    {metaDeleted
                        ? "This customer is deleted. Restore them before adding or changing linked locations."
                        : "This customer is read-only for your account. You cannot add or change linked locations."}
                </Alert>
            )}

            <PagedList
                columns={columns}
                headActions={headActions}
                itemActions={itemActions}
                filters={filters}
                rowIndicators={[PAGED_LIST_MAIN_LOCATION_ROW_INDICATOR]}
                defaultSortBy="isMainLocation"
                defaultSortOrder={SortOrder.descending}
                getUrl={`/api/sites/${encodeURIComponent(siteId)}/customers/${encodeURIComponent(customerId)}/location-associations/all`}
                keyColumn="id"
                rowFormatter={(item: ICustomerLocationAssociationRow) => item.locationName}
                refreshNumber={refreshNumber}
                persistanceKey={`CustomerLocationsPage.${customerId}`}
                rowActionsAriaLabel="Location association actions"
            />

            <AddCustomerLocationModal
                show={showAddModal}
                siteId={siteId}
                customerId={customerId}
                onClose={() => setShowAddModal(false)}
                onAssociated={() => {
                    setShowAddModal(false);
                    setRefreshNumber((n) => n + 1);
                }}
            />

            <EditCustomerLocationLabelModal
                show={editLabelRow !== null}
                siteId={siteId}
                customerId={customerId}
                association={
                    editLabelRow
                        ? {
                              id: editLabelRow.id,
                              locationName: editLabelRow.locationName,
                              label: editLabelRow.label,
                          }
                        : null
                }
                onClose={() => setEditLabelRow(null)}
                onSaved={() => setRefreshNumber((n) => n + 1)}
            />

            <ConfirmationModel
                show={confirmRow !== null}
                onClose={(confirmed) => {
                    if (confirmed) runRemove();
                    else setConfirmRow(null);
                }}
                title="Unlink location"
                body={
                    confirmRow
                        ? `Remove the link to "${confirmRow.locationName}" from this customer?`
                        : ""
                }
                buttonSuccessVariant="danger"
                buttonSuccessText="Unlink"
                buttonCancelText="Cancel"
            />

            <ConfirmationModel
                show={confirmSetMainRow !== null}
                onClose={(confirmed) => {
                    if (confirmed) runSetMain();
                    else setConfirmSetMainRow(null);
                }}
                title="Set primary address"
                body={
                    confirmSetMainRow
                        ? `Set "${confirmSetMainRow.locationName}" as the primary address for this customer? Any existing primary address will be cleared.`
                        : ""
                }
                buttonSuccessText="Set as primary"
                buttonCancelText="Cancel"
            />
        </div>
    );
};

export { CustomerLocationsView };
